import { loadStripe } from "@stripe/stripe-js";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

export function useStripe() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  /**
   * Tente de récupérer et d'exécuter une souscription sauvegardée dans le localStorage
   * Utile après une inscription pour continuer le processus de paiement
   */
  const continueWithSavedSubscription = async () => {
    try {
      const selectedPlanString = localStorage.getItem('selectedPlan');
      if (!selectedPlanString) return false;

      interface SavedPlan {
        tier: string;
        billingPeriod: 'monthly' | 'yearly';
        timestamp?: number;
      }
      
      const selectedPlan = JSON.parse(selectedPlanString) as SavedPlan;
      if (!selectedPlan || !selectedPlan.tier || !selectedPlan.billingPeriod) return false;

      // Vérifier que ce n'est pas un plan gratuit
      if (selectedPlan.tier.toLowerCase() === 'free') return false;

      // Vérifier que le plan n'est pas expiré (trop ancien)
      const now = Date.now();
      const timestamp = selectedPlan.timestamp || 0;
      const oneHour = 60 * 60 * 1000;
      if (now - timestamp > oneHour) {
        // Si le plan sélectionné a plus d'une heure, le supprimer
        localStorage.removeItem('selectedPlan');
        return false;
      }

      // Importation dynamique pour éviter les dépendances circulaires
      const { STRIPE_PRICES } = await import('../config/stripe');
      const priceId = STRIPE_PRICES[selectedPlan.tier.toLowerCase() as keyof typeof STRIPE_PRICES][selectedPlan.billingPeriod];
      
      if (!priceId) {
        console.error('Invalid price ID for the selected plan');
        return false;
      }

      console.log('Continuing with saved subscription plan:', selectedPlan);
      await createSubscription(priceId);
      return true;
    } catch (err) {
      console.error('Error continuing with saved subscription:', err);
      return false;
    }
  };

  const createSubscription = async (priceId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Vérifier l'authentification
      const { data: authData, error: authError } =
        await supabase.auth.getUser();
      if (authError) {
        throw new Error("Erreur d'authentification");
      }
      if (!authData.user) {
        // Stocker le plan sélectionné et rediriger vers l'inscription
        localStorage.setItem(
          "selectedPlan",
          JSON.stringify({
            priceId,
            timestamp: Date.now(),
          }),
        );
        navigate("/signup");
        return;
      }

      // Vérifier la session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error("Session invalide");
      }

      console.log("Création de la session de paiement...");
      
      // Configuration personnalisée pour éviter le problème CORS avec apikey
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`;
      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ priceId }),
      });

      if (!response.ok) {
        console.error("Erreur API:", await response.text());
        throw new Error("Erreur de communication avec le serveur de paiement");
      }

      const data = await response.json();
      if (!data?.sessionId) {
        throw new Error("Session de paiement invalide");
      }

      console.log("Session de paiement créée:", data.sessionId);

      // Initialiser Stripe
      const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
      if (!stripeKey) {
        throw new Error("Clé Stripe non configurée");
      }
      
      const stripe = await loadStripe(stripeKey);
      if (!stripe) {
        throw new Error("Erreur d'initialisation du système de paiement");
      }

      // Rediriger vers Stripe Checkout
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });

      if (stripeError) {
        throw new Error(
          stripeError.message ||
            "Erreur lors de la redirection vers le paiement",
        );
      }
    } catch (err) {
      console.error("Error creating subscription:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Une erreur est survenue lors de la création de l'abonnement",
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const manageSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      // Vérifier l'authentification
      const { data: authData, error: authError } =
        await supabase.auth.getUser();
      if (authError || !authData.user) {
        throw new Error("Non authentifié");
      }

      // Vérifier la session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error("Session invalide");
      }

      console.log("Création de la session du portail client...");

      // Configuration personnalisée pour éviter le problème CORS avec apikey
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-portal-session`;
      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        console.error("Erreur API:", await response.text());
        throw new Error("Erreur de communication avec le portail client");
      }

      const data = await response.json();
      if (!data?.url) {
        throw new Error("URL du portail client invalide");
      }

      window.location.href = data.url;
    } catch (err) {
      console.error("Error managing subscription:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Une erreur est survenue lors de l'accès au portail client",
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    createSubscription,
    continueWithSavedSubscription,
    manageSubscription,
    error,
    loading,
    clearError: () => setError(null),
  };
}